// ---------------------------------------------------------------------------
// Chart arithmetic (205): the numbers behind the Credits page's pictures,
// away from the SVG so a test can hold them. No library: three shapes
// (bars by day, a donut, bars by name) on the theme's own tokens.
// ---------------------------------------------------------------------------

/** The colours a series cycles through, as CSS custom properties the theme already defines. */
export const SERIES = ['--blue', '--teal', '--purple', '--amber', '--rose', '--green', '--red', '--ink-3'] as const;
export const seriesColor = (i: number) => `var(${SERIES[i % SERIES.length]})`;

/** A round ceiling for an axis: 1, 2, 5 × 10^n at or above the largest value. */
export function niceMax(max: number): number {
  if (!(max > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 2, 5, 10]) if (m * p >= max) return m * p;
  return 10 * p;
}

/** Every day from `days` ago through today, each with its credits, so a quiet day is a gap and not a missing bar. */
export function fillDays(rows: Array<{ day: string; credits: number }>, days: number, today = new Date()): Array<{ day: string; credits: number }> {
  const by = new Map(rows.map((r) => [r.day.slice(0, 10), r.credits]));
  const out: Array<{ day: string; credits: number }> = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    out.push({ day: key, credits: by.get(key) ?? 0 });
  }
  return out;
}

/** The biggest `n` rows and the rest folded into one, so a chart never has thirty slivers. */
export function topN<T extends { credits: number }>(rows: T[], n: number, fold: (rest: T[]) => T): T[] {
  const sorted = [...rows].sort((a, b) => b.credits - a.credits);
  if (sorted.length <= n) return sorted;
  const head = sorted.slice(0, n - 1);
  const rest = sorted.slice(n - 1);
  return [...head, fold(rest)];
}

export interface Slice { start: number; end: number; share: number }

/** The angles of a donut, clockwise from the top, as fractions of a turn. Zero rows give no slices. */
export function slices(values: number[]): Slice[] {
  const total = values.reduce((a, b) => a + Math.max(0, b), 0);
  if (total <= 0) return [];
  let at = 0;
  return values.map((v) => { const share = Math.max(0, v) / total; const s = { start: at, end: at + share, share }; at += share; return s; });
}

/** An SVG arc for a slice of a ring, from a fraction of a turn to another, clockwise from the top. */
export function arcPath(cx: number, cy: number, r: number, start: number, end: number): string {
  const full = end - start >= 0.9999;
  const e = full ? start + 0.9999 : end;
  const a0 = (start - 0.25) * 2 * Math.PI;
  const a1 = (e - 0.25) * 2 * Math.PI;
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  const large = e - start > 0.5 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

/** A short day label: the day of the month, and the month's name on the first of it or the first bar. */
export function dayLabel(iso: string, first: boolean): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDate();
  return day === 1 || first ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }) : String(day);
}

/** A whole number with a thousands separator, or a dash for nothing. */
export const sayCount = (n: number | null | undefined) => (n === null || n === undefined ? '—' : Math.round(n).toLocaleString());

/** How much of a grant a plan's month has used, in whole percent, held to 0–100. */
export function usedPercent(grant: number | null, planLeft: number): number {
  if (!grant || grant <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round(((grant - Math.max(0, planLeft)) / grant) * 100)));
}

/** What a month's grant buys of one example: the whole units of it, where a per-second price is a whole 8-second shot. */
export function buys(grant: number, credits: number, unit: string, seconds = 8): { count: number; what: string } {
  if (credits <= 0) return { count: 0, what: '' };
  if (unit === 'second') return { count: Math.floor(grant / (credits * seconds)), what: `${seconds}-second shots` };
  if (unit === 'k_tokens') return { count: Math.floor(grant / credits), what: 'thousand tokens' };
  if (unit === 'k_chars') return { count: Math.floor(grant / credits), what: 'thousand characters' };
  if (unit === 'minute') return { count: Math.floor(grant / credits), what: 'minutes' };
  return { count: Math.floor(grant / credits), what: '' };
}
